import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import StepChart from "../../lib/components/StepChart/index_d3";
import StateTimeline from "../../lib/components/StateTimeline";
import LineBarComposedChart_new from "../../lib/components/LineBarComposedChart_new";
import useStepChartReducer from "./hooks/useStepChartReducer";
import useEventsReducer from "./hooks/useEventsReducer";
import FilterBar from "../../lib/components/Filterbar/Filterbar";
import ChargerDisplay from "../../lib/components/ChargerDisplay";
import { useGlobalFilters } from "../../lib/context/GlobalFilterProvider";
import { useTimezone } from "../../lib/context/TimezoneProvider";
import useBarChartReducer from "./hooks/useBarChartReducer";
import useEnergyConsumptionReducer from "./hooks/useEnergyConsumptionHook/useEnergyConsumptionReducer";
import SlideOutPanel from "../ocpp_messages/SlideOutPanel";
import { getRange } from "../../lib/utils/utility";
import { DEFAULT_TIME_RANGE } from "../../lib/utils/constants";
import { Button } from "@mui/material";
import { RESET_ZOOM_BUTTON_SX } from "./constants";

export function ChargerMetrics({ embedded = true, headerActions = null, dateRange: dateRangeProp = null }) {
  const { serialNumber, dateRange: globalDateRange, siteId } = useGlobalFilters();
  const { effectiveTimezone } = useTimezone();
  const [localDateRange, setLocalDateRange] = useState(() => getRange(DEFAULT_TIME_RANGE));
  const dateRange = dateRangeProp || globalDateRange || localDateRange;
  const chartFilters = useMemo(
    () => ({ chargerId: serialNumber, timeRange: dateRange, siteId, siteTimezone: effectiveTimezone }),
    [serialNumber, dateRange, siteId, effectiveTimezone]
  );
  const { status } = useStepChartReducer(chartFilters);
  const { matrix } = useBarChartReducer(chartFilters);
  const energyConsumption = useEnergyConsumptionReducer(chartFilters);
  const { events: eventsState } = useEventsReducer(chartFilters);
  const [openChargerDetailsSlideout, setOpenChargerDetailsSlideout] = useState(false);
  const [zoomExtremes, setZoomExtremes] = useState(null);

  useEffect(() => {
    if (!dateRangeProp && globalDateRange) setLocalDateRange(globalDateRange);
  }, [dateRangeProp, globalDateRange]);

  const handleDateChange = range => {
    setLocalDateRange(range);
  };

  const handleOnRefresh = () => {};

  return (
    <div className={embedded ? "w-full overflow-visible" : "w-full h-screen overflow-hidden p-8"}>
      {!embedded && (
        <FilterBar
          onDateChange={handleDateChange}
          handleOnRefresh={handleOnRefresh}
          chargerDetails={true}
          openChargerDetailsSlideout={() => setOpenChargerDetailsSlideout(true)}
          isRefreshShown={true}
          isSerialNumberRequired={true}
          embedded={false}
        />
      )}
      {headerActions ? (
        headerActions({
          zoomExtremes,
          setZoomExtremes,
          openChargerDetailsSlideout,
          setOpenChargerDetailsSlideout,
          serialNumber,
        })
      ) : (
        <div className="flex items-center justify-end">
          {!embedded && (
            <ChargerDisplay serialNumber={serialNumber} onClick={() => setOpenChargerDetailsSlideout(true)} />
          )}
          <Button
            size="small"
            variant="outlined"
            disabled={!zoomExtremes}
            onClick={() => setZoomExtremes(null)}
            sx={RESET_ZOOM_BUTTON_SX}
          >
            Reset Zoom
          </Button>
        </div>
      )}
      <div className="flex gap-8 items-start h-[calc(100vh-200px)]">
        <div className="flex-1 flex flex-col gap-5 w-full h-full">
          <LineBarComposedChart_new
            data={matrix?.data}
            energyConsumptionData={energyConsumption?.state?.data}
            loading={matrix.isLoading && energyConsumption?.state?.isLoading}
            onZoomChange={setZoomExtremes}
            zoomExtremes={zoomExtremes}
            siteTimezone={effectiveTimezone}
          />
          <StepChart
            data={status?.data}
            loading={status?.isLoading}
            dateRange={dateRange}
            onZoomChange={setZoomExtremes}
            zoomExtremes={zoomExtremes}
            siteTimezone={effectiveTimezone}
          />
          <StateTimeline
            events={eventsState?.data}
            loading={eventsState?.isLoading}
            dateRange={dateRange}
            siteTimezone={effectiveTimezone}
            onZoomChange={setZoomExtremes}
            zoomExtremes={zoomExtremes}
          />
        </div>
      </div>
      {openChargerDetailsSlideout && (
        <SlideOutPanel open={openChargerDetailsSlideout} onClose={() => setOpenChargerDetailsSlideout(false)} />
      )}
    </div>
  );
}

ChargerMetrics.propTypes = {
  embedded: PropTypes.bool,
  headerActions: PropTypes.func,
  dateRange: PropTypes.array,
};

ChargerMetrics.defaultProps = {
  embedded: true,
  headerActions: null,
  dateRange: null,
};

export default ChargerMetrics;
